import { Box, Center, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import React from "react";
import { useFormikContext } from "formik";
import { CherryPayApi } from "../../api/models";
import { CollapsibleSection } from "./CollapsibleSection";
import { GeoLocationCard } from "./GeoLocationCard";
import { NotificationFormValues } from "./types";
import { CheckCircleFillIcon } from "../../styles/icons";
import { isEmptyStr } from "../../util/StringUtil";

interface GeoLocationsSectionProps {
  locations: CherryPayApi.GeoLocation[];
  isLoading: boolean;
  hasFetched: boolean;
  selectedLocationId: string | null;
  onSelectLocation: (locationId: string | null) => void;
  onLocationMessageSaved: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

export const GeoLocationsSection: React.FC<GeoLocationsSectionProps> = ({
  locations,
  isLoading,
  hasFetched,
  selectedLocationId,
  onSelectLocation,
  onLocationMessageSaved,
  isOpen,
  onToggle,
}) => {
  const { values } = useFormikContext<NotificationFormValues>();

  const overrideCount = locations.reduce((count, location) => {
    const overrideKey = location.Meta?.Id;
    if (!overrideKey) return count;
    const override = values.locationMessageOverrides?.[overrideKey];
    return !isEmptyStr(override) ? count + 1 : count;
  }, 0);

  const overrideSummary = `${overrideCount} of ${locations.length} locations overridden`;

  const rightAdornment =
    locations.length === 0 ? undefined : overrideCount > 0 ? (
      <HStack
        bg="riskRating.safe.bg"
        borderRadius="full"
        px={3}
        py={1}
        spacing={2}
      >
        <CheckCircleFillIcon color="riskRating.safe.icon" boxSize={4} />
        <Text fontSize="sm" color="riskRating.safe.icon">
          {overrideSummary}
        </Text>
      </HStack>
    ) : (
      <Box bg="cherryBackground.tag" borderRadius="full" px={3} py={1}>
        <Text fontSize="sm" color="gray.700">
          {overrideSummary}
        </Text>
      </Box>
    );

  return (
    <CollapsibleSection
      title="Geo-Locations"
      titleTooltip="Geo-fence notifications are currently only supported on Apple Wallet passes. Google Wallet does not support location-triggered messages."
      rightAdornment={rightAdornment}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <VStack w="100%" alignItems="stretch" spacing={3} pb={4}>
        {isLoading && (
          <Center py={6}>
            <Spinner size="md" color="gray.500" />
          </Center>
        )}
        {!isLoading && hasFetched && locations.length === 0 && (
          <Text fontSize="sm" color="gray.600">
            No geo-locations configured. Please contact support to enable this
            feature.
          </Text>
        )}
        {!isLoading && locations.length > 0 && (
          <Text fontSize="sm" color="gray.600">
            Override default messages for this campaign. Click &apos;Edit
            Message&apos; to edit.
          </Text>
        )}
        {!isLoading &&
          locations.map((location) => (
            <GeoLocationCard
              key={location.GeoLocationId}
              location={location}
              isSelected={selectedLocationId === location.GeoLocationId}
              onSelect={() => onSelectLocation(location.GeoLocationId)}
              onMessageSaved={onLocationMessageSaved}
            />
          ))}
      </VStack>
    </CollapsibleSection>
  );
};
